import { useState } from 'react';
import { Empty, Segmented, Tooltip } from 'antd';
import type { InvestorInvestmentMetrics, InvestorAssetClass } from '@mygate/common';
import { decimal, euro, integer, percentage } from './analytics/AnalyticsPrimitives';

const assetClassDisplay = {
  CRYPTO: { label: 'Krypto', color: '#087f79' },
  ETF: { label: 'ETFs', color: '#76a894' },
  EQUITY: { label: 'Aktien', color: '#93adc9' },
  BOND: { label: 'Anleihen', color: '#c7aa6a' },
  FUND: { label: 'Fonds', color: '#82936d' },
  COMMODITY: { label: 'Rohstoffe', color: '#ac96bd' },
  DERIVATIVE: { label: 'Derivate', color: '#bd8f84' },
  ETN: { label: 'ETNs', color: '#709aa4' },
  UNKNOWN: { label: 'Nicht klassifiziert', color: '#b7bdb6' },
} satisfies Record<InvestorAssetClass, { label: string; color: string }>;

const donutRadius = 48;
const donutCircumference = 2 * Math.PI * donutRadius;
// Width available for the centre text inside the ring (viewBox units), with a margin to the inner edge.
const donutTextWidth = 70;

/** SVG in a fixed viewBox: ring and centre text scale together, independent of viewport and browser. */
function AllocationDonut({ data, metric }: { data: InvestorInvestmentMetrics; metric: 'value' | 'holders' }) {
  const value = metric === 'value' ? euro.format(data.totalValueEur) : integer.format(data.investedUsers);
  // Bold tabular digits are about 0.6em wide; shrink long amounts instead of letting them cross the ring.
  const valueSize = Math.min(14, donutTextWidth / (0.6 * value.length));
  let offset = 0;
  const segments =
    metric === 'value' && data.totalValueEur > 0
      ? data.allocation.map(item => {
          const length = (item.valueEur / data.totalValueEur) * donutCircumference;
          const segment = { ...item, length, offset };
          offset += length;
          return segment;
        })
      : [];
  return (
    <svg className="analytics-donut" viewBox="0 0 120 120" aria-hidden="true">
      <circle cx="60" cy="60" r={donutRadius} className="analytics-donut-track" />
      {segments.map(segment => (
        <Tooltip
          key={segment.assetClass}
          title={
            <>
              <strong>{assetClassDisplay[segment.assetClass].label}</strong>
              <br />
              {euro.format(segment.valueEur)} · {percentage(segment.valueEur, data.totalValueEur)}
              <br />
              {segment.holders} von {data.investedUsers} Anlegern
            </>
          }
        >
          <circle
            cx="60"
            cy="60"
            r={donutRadius}
            className="analytics-donut-segment"
            stroke={assetClassDisplay[segment.assetClass].color}
            strokeDasharray={`${segment.length} ${donutCircumference - segment.length}`}
            strokeDashoffset={-segment.offset}
            transform="rotate(-90 60 60)"
          />
        </Tooltip>
      ))}
      <text x="60" y="53" className="analytics-donut-label">
        {metric === 'value' ? 'Anlagevolumen' : 'Nutzer mit Bestand'}
      </text>
      <text x="60" y="71" className="analytics-donut-value" style={{ fontSize: valueSize }}>
        {value}
      </text>
    </svg>
  );
}

export function AssetAllocation({ data }: { data: InvestorInvestmentMetrics | null }) {
  const [metric, setMetric] = useState<'value' | 'holders'>('value');
  if (!data) return <Empty description="Für diesen Stichtag fehlen ausreichend aktuelle Bestände." />;

  return (
    <>
      <Segmented
        size="small"
        aria-label="Assetklassen vergleichen nach"
        value={metric}
        onChange={setMetric}
        options={[
          { label: 'Anlagevolumen', value: 'value' },
          { label: 'Nutzer', value: 'holders' },
        ]}
      />
      <div className="analytics-allocation">
        <AllocationDonut data={data} metric={metric} />
        <ul className="analytics-allocation-list" aria-label="Assetklassen und Anteile">
          {data.allocation.map(item => {
            const display = assetClassDisplay[item.assetClass];
            const count = metric === 'value' ? item.valueEur : item.holders;
            const total = metric === 'value' ? data.totalValueEur : data.investedUsers;
            return (
              <li key={item.assetClass}>
                <div>
                  <span className="analytics-dot" style={{ background: display.color }} />
                  <span>{display.label}</span>
                  <strong>{percentage(count, total)}</strong>
                </div>
                <div className="analytics-allocation-track" aria-hidden="true">
                  <span style={{ width: `${total ? (count / total) * 100 : 0}%`, background: display.color }} />
                </div>
                <small>{metric === 'value' ? euro.format(count) : `${count} von ${data.investedUsers} Nutzern`}</small>
              </li>
            );
          })}
        </ul>
      </div>
      {data.investedUsers === 0 && <p>In den erfassten Beständen sind keine positiven Anlagepositionen vorhanden.</p>}
    </>
  );
}

const count = (value: number) => (Number.isInteger(value) ? integer.format(value) : decimal.format(value));

function DistributionRows({
  title,
  rows,
  investors,
}: {
  title: string;
  rows: Array<{ label: string; users: number }>;
  investors: number;
}) {
  return (
    <section className="analytics-breadth">
      <h4>{title}</h4>
      <ul>
        {rows.map(row => (
          <li key={row.label}>
            <span>{row.label}</span>
            <div className="analytics-allocation-track" aria-hidden="true">
              <span style={{ width: `${(row.users / investors) * 100}%`, background: '#619a85' }} />
            </div>
            <small>
              {row.users} Anleger · {percentage(row.users, investors)}
            </small>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function PortfolioDepth({ data }: { data: InvestorInvestmentMetrics | null }) {
  if (!data || data.breadthUsers === 0 || data.medianAssets === null || data.medianClasses === null) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Für keinen Anleger ist der Bestand vollständig erfasst."
      />
    );
  }
  const classRows = [1, 2, 3, 4].map(classes => ({
    label: classes === 1 ? 'Nur eine Klasse' : classes === 4 ? '4+ Klassen' : `${classes} Klassen`,
    users: data.classDistribution
      .filter(item => (classes === 4 ? item.count >= 4 : item.count === classes))
      .reduce((sum, item) => sum + item.users, 0),
  }));
  const assetRows = data.assetDistribution.map(item => ({
    label: `${item.label} ${item.label === '1' ? 'Asset' : 'Assets'}`,
    users: item.users,
  }));
  const excluded = data.investedUsers - data.breadthUsers;
  return (
    <>
      <p className="analytics-breadth-lead">
        Ein typischer Anleger hält{' '}
        <strong>
          {count(data.medianAssets)} {data.medianAssets === 1 ? 'Asset' : 'Assets'}
        </strong>{' '}
        aus{' '}
        <strong>
          {count(data.medianClasses)} {data.medianClasses === 1 ? 'Anlageklasse' : 'Anlageklassen'}
        </strong>
        .{data.assetsPerUser !== null && <small> Median · Durchschnitt {count(data.assetsPerUser)} Assets</small>}
      </p>
      <DistributionRows title="Anlageklassen je Anleger" rows={classRows} investors={data.breadthUsers} />
      <DistributionRows title="Verschiedene Assets je Anleger" rows={assetRows} investors={data.breadthUsers} />
      <p className="analytics-chart-basis">
        Grundlage: {data.breadthUsers} von {data.investedUsers} Anlegern.
        {excluded > 0 && ` Bei ${excluded} ist der Bestand nicht vollständig erfasst, sie fehlen hier.`}
        {data.allocation.some(item => item.assetClass === 'UNKNOWN') &&
          ' „Nicht klassifiziert“ zählt als eigene Klasse.'}
      </p>
    </>
  );
}
